import { useCallback } from 'react';
import { appliquerLangue } from '../lib/i18n';
import { useNavigate } from 'react-router-dom';
import { useUserStore, type User } from '@/stores/userStore';
import { apiClient } from '@/lib/apiClient';
import { ROUTES } from '@/lib/routes';

/**
 * Hook useAuth — wrapper logique auth au-dessus du userStore.
 * Expose login, logout, fetchMe, et les getters du store.
 */
export function useAuth() {
  const user = useUserStore((s) => s.user);
  const accessToken = useUserStore((s) => s.accessToken);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const setUser = useUserStore((s) => s.setUser);
  const setAccessToken = useUserStore((s) => s.setAccessToken);
  const storeLogout = useUserStore((s) => s.logout);
  const hasPermission = useUserStore((s) => s.hasPermission);
  const hasAllPermissions = useUserStore((s) => s.hasAllPermissions);

  const navigate = useNavigate();

  /**
   * Récupère le profil utilisateur depuis /auth/me et le stocke.
   * Normalise roles : backend renvoie [{code, name}] → on extrait les codes string[].
   */
  const fetchMe = useCallback(async (): Promise<User> => {
    const res = await apiClient.get<Record<string, unknown>>('/auth/me');
    const raw = res.data;
    const me: User = {
      ...(raw as unknown as User),
      roles: Array.isArray(raw.roles)
        ? (raw.roles as Array<unknown>).map((r) =>
            typeof r === 'string' ? r : (r as { code: string }).code
          )
        : [],
    };
    setUser(me);
    // `#796` — la langue du compte pilote l interface (immediat).
    if (me.language === 'en' || me.language === 'fr') appliquerLangue(me.language);
    return me;
  }, [setUser]);

  /**
   * Déconnexion : appel /auth/logout + clear store + redirect /login.
   */
  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Logout côté backend optionnel — on nettoie le store quoi qu'il arrive
    }
    storeLogout();
    navigate(ROUTES.login);
  }, [storeLogout, navigate]);

  return {
    user,
    accessToken,
    isAuthenticated,
    setAccessToken,
    fetchMe,
    logout,
    hasPermission,
    hasAllPermissions,
  };
}
